import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, PawPrint } from 'lucide-react';
import { CONTACT_INFO, categories, CARD_DESCRIPTOR } from '../../data/mock';

// Inline SVG payment-method logos (Visa, Mastercard, Amex, Discover)
const PaymentLogos = () => (
  <div className="flex flex-wrap items-center gap-2">
    {/* Visa */}
    <div className="h-8 w-14 bg-white rounded-md border border-gray-200 flex items-center justify-center px-1.5 shadow-sm" title="Visa">
      <svg viewBox="0 0 48 32" className="h-6 w-auto" aria-label="Visa">
        <text x="24" y="22" textAnchor="middle" fontSize="14" fontWeight="900" fill="#1A1F71" fontFamily="Arial, sans-serif" fontStyle="italic">VISA</text>
      </svg>
    </div>
    {/* Mastercard */}
    <div className="h-8 w-14 bg-white rounded-md border border-gray-200 flex items-center justify-center shadow-sm" title="Mastercard">
      <svg viewBox="0 0 48 32" className="h-6 w-auto" aria-label="Mastercard">
        <circle cx="19" cy="16" r="9" fill="#EB001B" />
        <circle cx="29" cy="16" r="9" fill="#F79E1B" />
        <path d="M24 9.5 a9 9 0 0 0 0 13" fill="#FF5F00" />
      </svg>
    </div>
    {/* American Express */}
    <div className="h-8 w-14 bg-[#2E77BB] rounded-md flex items-center justify-center px-1 border border-[#1F5E9C] shadow-sm" title="American Express">
      <svg viewBox="0 0 48 32" className="h-6 w-auto" aria-label="American Express">
        <rect x="0" y="0" width="48" height="32" fill="#2E77BB" />
        <text x="24" y="14" textAnchor="middle" fontSize="6.5" fontWeight="900" fill="#FFFFFF" fontFamily="Arial, sans-serif" letterSpacing="0.6">AMERICAN</text>
        <text x="24" y="22" textAnchor="middle" fontSize="6.5" fontWeight="900" fill="#FFFFFF" fontFamily="Arial, sans-serif" letterSpacing="0.6">EXPRESS</text>
        <text x="24" y="30" textAnchor="middle" fontSize="5" fontWeight="800" fill="#FFFFFF" fontFamily="Arial, sans-serif" letterSpacing="1.2">AMEX</text>
      </svg>
    </div>
    {/* Discover */}
    <div className="h-8 w-14 bg-white rounded-md border border-gray-200 flex items-center justify-center px-1 shadow-sm" title="Discover">
      <svg viewBox="0 0 60 32" className="h-5 w-auto" aria-label="Discover">
        <text x="2" y="20" fontSize="10" fontWeight="900" fill="#000000" fontFamily="Arial, sans-serif">DISC</text>
        <circle cx="50" cy="16" r="7" fill="#FF6000" />
      </svg>
    </div>
  </div>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-brand-brown text-white mt-12">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">

          {/* Brand Column */}
          <div className="col-span-2 md:col-span-4 lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-full bg-brand-orange flex items-center justify-center shadow-lg ring-4 ring-brand-brown-light/20">
                <PawPrint className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white tracking-tight font-display">
                Paw Supplies Group
              </span>
            </Link>
            <p className="text-white/70 text-sm leading-relaxed mb-5 max-w-sm">
              Premium pet accessories and equipment for the dogs and cats who deserve the finest. Shipped fast from the U.S.A.
            </p>
            <div className="space-y-2.5 text-sm">
              <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-start gap-2.5 text-white/80 hover:text-brand-orange">
                <Phone className="w-4 h-4 mt-0.5 text-brand-orange" />
                <span>{CONTACT_INFO.phone}</span>
              </a>
              <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-start gap-2.5 text-white/80 hover:text-brand-orange">
                <Mail className="w-4 h-4 mt-0.5 text-brand-orange" />
                <span className="break-all">{CONTACT_INFO.email}</span>
              </a>
              <div className="flex items-start gap-2.5 text-white/80">
                <MapPin className="w-4 h-4 mt-0.5 text-brand-orange flex-shrink-0" />
                <span>
                  {CONTACT_INFO.address.street}<br />
                  {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}
                </span>
              </div>
            </div>
          </div>

          {/* Shop */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-[0.2em] mb-5 font-display">Shop</h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/shop" className="text-white/70 hover:text-brand-orange text-sm transition-colors">All Products</Link>
              </li>
              {categories.map((cat) => (
                <li key={cat.id}>
                  <Link to={`/shop?category=${cat.slug}`} className="text-white/70 hover:text-brand-orange text-sm transition-colors">
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-[0.2em] mb-5 font-display">Customer Care</h4>
            <ul className="space-y-2.5">
              {[
                { name: 'Shipping Info', path: '/shipping' },
                { name: 'Returns & Refunds', path: '/returns' },
                { name: 'FAQ', path: '/faq' },
                { name: 'Privacy Policy', path: '/privacy' },
                { name: 'Terms & Conditions', path: '/terms' },
              ].map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-white/70 hover:text-brand-orange text-sm transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* About */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-[0.2em] mb-5 font-display">Company</h4>
            <ul className="space-y-2.5">
              <li><Link to="/about" className="text-white/70 hover:text-brand-orange text-sm transition-colors">About Us</Link></li>
              <li><Link to="/why-choose-us" className="text-white/70 hover:text-brand-orange text-sm transition-colors">Why Choose Us</Link></li>
              <li><Link to="/our-mission" className="text-white/70 hover:text-brand-orange text-sm transition-colors">Our Mission</Link></li>
              <li><Link to="/quality-guarantee" className="text-white/70 hover:text-brand-orange text-sm transition-colors">Quality Guarantee</Link></li>
              <li><Link to="/contact" className="text-white/70 hover:text-brand-orange text-sm transition-colors">Contact Us</Link></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Left */}
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-sm text-white/70">
              <span>&copy; {currentYear} Paw Supplies Group</span>
              <span className="hidden sm:block text-white/30">|</span>
              <span>{CONTACT_INFO.corpName}</span>
            </div>

            {/* Center - Payment Logos */}
            <div className="flex items-center gap-3">
              <span className="text-white/60 text-xs font-semibold uppercase tracking-wider">We Accept</span>
              <PaymentLogos />
            </div>

            {/* Right */}
            <div className="text-xs text-white/60">
              Charges appear as &quot;<span className="font-semibold text-white">{CARD_DESCRIPTOR}</span>&quot;
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
